/**
 * <searchable-select> Web Component
 *
 * A searchable dropdown select for filtering through large lists.
 * Supports grouping, keyboard navigation, and text highlighting.
 *
 * Usage:
 *   <searchable-select placeholder="Search..."></searchable-select>
 *
 * Attributes:
 *   - placeholder: Placeholder text for the input
 *   - disabled: Disable the component
 *
 * Events:
 *   - change: Fired when selection changes
 *     detail: { value, label, group }
 *   - open: Fired when dropdown opens
 *   - close: Fired when dropdown closes
 *
 * Setting options (via JavaScript):
 *   picker.options = [
 *     { value: 'L1', label: 'L1', group: 'Lakeside Tent Sites' },
 *     { value: 'C5', label: 'Cabin 5', group: 'Cabins' },
 *   ];
 *
 * Form Integration:
 *   Uses ElementInternals for native form participation.
 */
class SearchableSelectElement extends HTMLElement {
  static formAssociated = true;
  static observedAttributes = ['placeholder', 'disabled'];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.internals = this.attachInternals();

    // State
    this._options = [];
    this._filteredOptions = [];
    this._selectedValue = null;
    this._selectedLabel = null;
    this._pendingValue = null;
    this._isOpen = false;
    this._highlightedIndex = -1;
    this._searchQuery = '';
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    // luboo-dialog moves hosts around; render() rebuilds the shadow input
    // and would otherwise leave the placeholder even when a value is set.
    if (this._selectedValue) this.updateDisplay();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'placeholder') {
      const input = this.shadowRoot.querySelector('.search-input');
      if (input) input.placeholder = newValue || 'Search...';
    }
    if (name === 'disabled') {
      const input = this.shadowRoot.querySelector('.search-input');
      if (input) input.disabled = this.hasAttribute('disabled');
    }
  }

  // Public API: Set options
  set options(opts) {
    this._options = opts || [];
    this._filteredOptions = [...this._options];
    this.renderOptions();
    if (this._pendingValue != null) {
      this.value = this._pendingValue;
    } else if (this._selectedValue) {
      this.updateDisplay();
    }
  }

  get options() {
    return this._options;
  }

  // Public API: Get/Set value
  get value() {
    return this._selectedValue;
  }

  set value(val) {
    if (val == null || val === '') {
      this._pendingValue = null;
      this._selectedValue = null;
      this._selectedLabel = null;
      this.updateDisplay();
      this.internals.setFormValue(null);
      return;
    }
    const want = String(val);
    const option = this._options.find((o) => String(o.value) === want);
    if (!option) {
      this._pendingValue = want;
      return;
    }
    this._pendingValue = null;
    this._selectedValue = option.value;
    this._selectedLabel = option.label;
    this.updateDisplay();
    this.internals.setFormValue(option.value);
  }

  // Public API: Get selected label
  get selectedLabel() {
    return this._selectedLabel;
  }

  // Public API: Reset
  reset() {
    this._selectedValue = null;
    this._selectedLabel = null;
    this._pendingValue = null;
    this._searchQuery = '';
    this._filteredOptions = [...this._options];
    this.updateDisplay();
    this.renderOptions();
    this.internals.setFormValue(null);
  }

  render() {
    const placeholder = this.getAttribute('placeholder') || 'Search...';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          /* Default values (overridable via CSS custom properties) */
          --ss-field-bg: var(--lb-surface, #fff);
          --ss-field-border: var(--lb-line, #ccc);
          --ss-field-border-focus: var(--lb-accent, #007bff);
          --ss-field-radius: 4px;
          --ss-field-padding: 10px;

          --ss-text-color: var(--lb-text, #333);
          --ss-text-muted: var(--lb-muted, #666);
          --ss-text-placeholder: var(--lb-muted, #999);

          --ss-primary: var(--lb-accent, #007bff);

          --ss-dropdown-bg: var(--lb-surface, #fff);
          --ss-dropdown-border: var(--lb-line, #ccc);
          --ss-dropdown-shadow: rgba(0, 0, 0, 0.15);

          --ss-option-hover-bg: var(--lb-surface-2, #f5f5f5);
          --ss-option-selected-bg: var(--lb-accent, #007bff);
          --ss-option-selected-text: var(--lb-accent-fg, #fff);

          --ss-group-bg: var(--lb-surface-2, #f0f0f0);
          --ss-group-text: var(--lb-muted, #666);

          --ss-highlight-bg: rgba(255, 200, 0, 0.3);

          display: block;
          position: relative;
          font-family: inherit;
        }

        .field {
          display: flex;
          align-items: center;
          background: var(--ss-field-bg);
          border: 1px solid var(--ss-field-border);
          border-radius: var(--ss-field-radius);
          cursor: text;
          transition: border-color 0.15s;
        }

        .field:focus-within {
          border-color: var(--ss-field-border-focus);
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
        }

        .search-input {
          flex: 1;
          padding: var(--ss-field-padding) 12px;
          border: none;
          background: transparent;
          font-size: 1rem;
          color: var(--ss-text-color);
          outline: none;
          min-width: 0;
        }

        .search-input::placeholder {
          color: var(--ss-text-placeholder);
        }

        .search-input:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .clear-button {
          display: none;
          padding: 8px;
          border: none;
          background: transparent;
          cursor: pointer;
          color: var(--ss-text-muted);
          line-height: 1;
        }

        .clear-button:hover {
          color: var(--ss-text-color);
        }

        .clear-button.visible {
          display: block;
        }

        .dropdown-arrow {
          padding: 8px;
          color: var(--ss-text-muted);
          pointer-events: none;
        }

        .dropdown {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 4px;
          background: var(--ss-dropdown-bg);
          border: 1px solid var(--ss-dropdown-border);
          border-radius: var(--ss-field-radius);
          box-shadow: 0 4px 12px var(--ss-dropdown-shadow);
          z-index: 1000;
          max-height: 300px;
          overflow-y: auto;
        }

        .dropdown.open {
          display: block;
        }

        .options-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .group-header {
          padding: 8px 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--ss-group-text);
          background: var(--ss-group-bg);
          position: sticky;
          top: 0;
        }

        .option {
          padding: var(--ss-field-padding) 12px;
          cursor: pointer;
          color: var(--ss-text-color);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .option:hover,
        .option.highlighted {
          background: var(--ss-option-hover-bg);
        }

        .option.selected {
          background: var(--ss-option-selected-bg);
          color: var(--ss-option-selected-text);
        }

        .option.selected:hover,
        .option.selected.highlighted {
          background: var(--ss-option-selected-bg);
          filter: brightness(1.1);
        }

        .option-label {
          flex: 1;
        }

        .option-group-badge {
          font-size: 0.7rem;
          padding: 2px 6px;
          border-radius: 3px;
          background: var(--ss-group-bg);
          color: var(--ss-group-text);
        }

        .option.selected .option-group-badge {
          background: rgba(255, 255, 255, 0.2);
          color: inherit;
        }

        .highlight {
          background: var(--ss-highlight-bg);
          border-radius: 2px;
        }

        .no-results {
          padding: 16px;
          text-align: center;
          color: var(--ss-text-muted);
          font-size: 0.9rem;
        }

        /* Scrollbar styling */
        .dropdown::-webkit-scrollbar {
          width: 8px;
        }

        .dropdown::-webkit-scrollbar-track {
          background: transparent;
        }

        .dropdown::-webkit-scrollbar-thumb {
          background: var(--ss-field-border);
          border-radius: 4px;
        }

        .dropdown::-webkit-scrollbar-thumb:hover {
          background: var(--ss-text-muted);
        }
      </style>

      <div class="field">
        <input type="text" class="search-input" placeholder="${placeholder}" autocomplete="off">
        <button type="button" class="clear-button" title="Clear selection">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
        <span class="dropdown-arrow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </span>
      </div>

      <div class="dropdown">
        <ul class="options-list"></ul>
      </div>
    `;
  }

  attachEventListeners() {
    const input = this.shadowRoot.querySelector('.search-input');
    const dropdown = this.shadowRoot.querySelector('.dropdown');
    const clearBtn = this.shadowRoot.querySelector('.clear-button');
    const field = this.shadowRoot.querySelector('.field');

    // Focus opens dropdown
    input.addEventListener('focus', () => {
      this.openDropdown();
    });

    // Click on field focuses input
    field.addEventListener('click', () => {
      input.focus();
    });

    // Input for filtering
    input.addEventListener('input', (e) => {
      this._searchQuery = e.target.value;
      this.filterOptions();
      this.openDropdown();
    });

    // Keyboard navigation
    input.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });

    // Clear button
    clearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.reset();
      input.focus();
    });

    // Close on outside click
    // Use composedPath() to correctly detect clicks across Shadow DOM boundaries
    // (needed when component is nested inside another Shadow DOM host)
    document.addEventListener('click', (e) => {
      if (!e.composedPath().includes(this) && this._isOpen) {
        this.closeDropdown();
      }
    });
  }

  openDropdown() {
    this._isOpen = true;
    this.shadowRoot.querySelector('.dropdown').classList.add('open');
    this._highlightedIndex = -1;
    this.dispatchEvent(new CustomEvent('open'));
  }

  closeDropdown() {
    this._isOpen = false;
    this.shadowRoot.querySelector('.dropdown').classList.remove('open');
    this._highlightedIndex = -1;

    // If there's a selection, restore the label in the input
    const input = this.shadowRoot.querySelector('.search-input');
    if (this._selectedValue && !this._searchQuery) {
      input.value = this._selectedLabel || '';
    }

    this.dispatchEvent(new CustomEvent('close'));
  }

  filterOptions() {
    const query = this._searchQuery.toLowerCase().trim();

    if (!query) {
      this._filteredOptions = [...this._options];
    } else {
      this._filteredOptions = this._options.filter((opt) => {
        const labelMatch = opt.label.toLowerCase().includes(query);
        const groupMatch = opt.group && opt.group.toLowerCase().includes(query);
        return labelMatch || groupMatch;
      });
    }

    this._highlightedIndex = this._filteredOptions.length > 0 ? 0 : -1;
    this.renderOptions();
  }

  renderOptions() {
    const list = this.shadowRoot?.querySelector('.options-list');
    if (!list) return;
    list.innerHTML = '';

    if (this._filteredOptions.length === 0) {
      list.innerHTML = '<li class="no-results">No matches found</li>';
      return;
    }

    const query = this._searchQuery.toLowerCase().trim();

    // Group options by group name
    const grouped = new Map();
    const ungrouped = [];

    this._filteredOptions.forEach((opt) => {
      if (opt.group) {
        if (!grouped.has(opt.group)) {
          grouped.set(opt.group, []);
        }
        grouped.get(opt.group).push(opt);
      } else {
        ungrouped.push(opt);
      }
    });

    let globalIndex = 0;

    // Render grouped options
    grouped.forEach((options, groupName) => {
      // Group header
      const header = document.createElement('li');
      header.className = 'group-header';
      header.textContent = groupName;
      list.appendChild(header);

      // Options in group
      options.forEach((opt) => {
        const li = this.createOptionElement(opt, globalIndex, query);
        list.appendChild(li);
        globalIndex++;
      });
    });

    // Render ungrouped options
    if (ungrouped.length > 0 && grouped.size > 0) {
      const header = document.createElement('li');
      header.className = 'group-header';
      header.textContent = 'Other';
      list.appendChild(header);
    }

    ungrouped.forEach((opt) => {
      const li = this.createOptionElement(opt, globalIndex, query);
      list.appendChild(li);
      globalIndex++;
    });

    this.updateHighlight();
  }

  createOptionElement(opt, index, query) {
    const li = document.createElement('li');
    li.className = 'option';
    li.dataset.index = index;
    li.dataset.value = opt.value;

    if (opt.value === this._selectedValue) {
      li.classList.add('selected');
    }

    // Label with highlighting
    const labelSpan = document.createElement('span');
    labelSpan.className = 'option-label';
    labelSpan.innerHTML = this.highlightMatch(opt.label, query);
    li.appendChild(labelSpan);

    // Group badge (if showing filtered results from group search)
    if (opt.group && query && opt.group.toLowerCase().includes(query) && !opt.label.toLowerCase().includes(query)) {
      const badge = document.createElement('span');
      badge.className = 'option-group-badge';
      badge.textContent = opt.group;
      li.appendChild(badge);
    }

    // Use mousedown instead of click to fire before blur
    // This prevents the dropdown from closing before selection completes
    li.addEventListener('mousedown', (e) => {
      e.preventDefault(); // Prevent focus loss
      this.selectOption(opt);
    });

    // Hover to highlight
    li.addEventListener('mouseenter', () => {
      this._highlightedIndex = index;
      this.updateHighlight();
    });

    return li;
  }

  highlightMatch(text, query) {
    if (!query) return text;

    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  updateHighlight() {
    const options = this.shadowRoot.querySelectorAll('.option');
    options.forEach((opt) => {
      opt.classList.toggle('highlighted', parseInt(opt.dataset.index) === this._highlightedIndex);
    });

    // Scroll highlighted option into view
    const highlighted = this.shadowRoot.querySelector('.option.highlighted');
    if (highlighted) {
      highlighted.scrollIntoView({ block: 'nearest' });
    }
  }

  handleKeydown(e) {
    const visibleOptions = this._filteredOptions;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!this._isOpen) {
          this.openDropdown();
        } else {
          this._highlightedIndex = Math.min(this._highlightedIndex + 1, visibleOptions.length - 1);
          this.updateHighlight();
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (this._isOpen) {
          this._highlightedIndex = Math.max(this._highlightedIndex - 1, 0);
          this.updateHighlight();
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (this._isOpen && this._highlightedIndex >= 0) {
          const opt = this._filteredOptions[this._highlightedIndex];
          if (opt) this.selectOption(opt);
        }
        break;

      case 'Escape':
        e.preventDefault();
        this.closeDropdown();
        break;

      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  selectOption(opt) {
    this._selectedValue = opt.value;
    this._selectedLabel = opt.label;
    this._searchQuery = '';

    this.updateDisplay();
    this.closeDropdown();

    // Update form value
    this.internals.setFormValue(opt.value);

    // Dispatch change event
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          value: opt.value,
          label: opt.label,
          group: opt.group,
        },
      })
    );
  }

  updateDisplay() {
    const input = this.shadowRoot?.querySelector('.search-input');
    const clearBtn = this.shadowRoot?.querySelector('.clear-button');
    if (!input) return;

    if (this._selectedValue) {
      input.value = this._selectedLabel || '';
      if (clearBtn) clearBtn.classList.add('visible');
    } else {
      input.value = '';
      if (clearBtn) clearBtn.classList.remove('visible');
    }
  }
}

export { SearchableSelectElement };
